// @ts-nocheck
import { Stack, Title, Text, SimpleGrid, Code, Badge } from '@mantine/core';
import { Card } from '../../../../components/ui';
import { DataTableWidget } from '../../../../components/advanced/Widgets';
import {
  mockUsers,
  mockTransactions,
  mockProducts,
  mockTasks,
} from '../../mockData/widgets';

export default function DataTableWidgetsPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Data Table Widgets</Title>
        <Text c="dimmed" size="sm">
          Enhanced data table widgets with search, filtering, pagination, and CSV/Excel export
          functionality. Perfect for displaying large datasets with interactive exploration.
        </Text>
      </Stack>

      {/* User Management Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>User Management Table</Title>
          <Text size="sm" c="dimmed">
            Comprehensive user table with role/status badges, search, and filter presets.
          </Text>

          <DataTableWidget
            title="User Management"
            subtitle="Active users in the system"
            data={mockUsers}
            columns={[
              { key: 'name', header: 'Name', sortable: true },
              { key: 'email', header: 'Email', sortable: true },
              {
                key: 'role',
                header: 'Role',
                sortable: true,
                render: (value) => (
                  <Badge
                    variant="light"
                    color={
                      value === 'admin'
                        ? 'red'
                        : value === 'manager'
                          ? 'blue'
                          : value === 'user'
                            ? 'green'
                            : 'gray'
                    }
                  >
                    {value}
                  </Badge>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                sortable: true,
                render: (value) => (
                  <Badge
                    variant="dot"
                    color={
                      value === 'active'
                        ? 'green'
                        : value === 'inactive'
                          ? 'gray'
                          : value === 'pending'
                            ? 'yellow'
                            : 'red'
                    }
                  >
                    {value}
                  </Badge>
                ),
              },
              { key: 'department', header: 'Department', sortable: true },
              {
                key: 'tasksCompleted',
                header: 'Tasks',
                sortable: true,
                align: 'right' as const,
              },
            ]}
            filterPresets={[
              { label: 'Active Users', filter: (user) => user.status === 'active' },
              { label: 'Admins', filter: (user) => user.role === 'admin' },
              { label: 'Managers', filter: (user) => user.role === 'manager' },
              { label: 'Engineering', filter: (user) => user.department === 'Engineering' },
              { label: 'High Performers', filter: (user) => user.performance >= 90 },
            ]}
            searchable
            searchKeys={['name', 'email', 'department']}
            exportable
            exportFilename="users-export"
            pageSize={10}
            onRowClick={(user) => console.log('Clicked user:', user)}
          />

          <Code block>
{`<DataTableWidget
  title="User Management"
  data={users}
  columns={[
    { key: 'name', header: 'Name', sortable: true },
    {
      key: 'status',
      header: 'Status',
      render: (value) => <Badge>{value}</Badge>
    }
  ]}
  filterPresets={[
    { label: 'Active', filter: (u) => u.status === 'active' }
  ]}
  searchable
  searchKeys={['name', 'email']}
  exportable
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Transaction Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Transaction Data Table</Title>
          <Text size="sm" c="dimmed">
            Financial transaction table with formatted amounts, dates, and status filtering.
          </Text>

          <DataTableWidget
            title="Recent Transactions"
            subtitle="Last 200 transactions"
            data={mockTransactions}
            columns={[
              { key: 'id', header: 'ID', sortable: true, width: 120 },
              {
                key: 'date',
                header: 'Date',
                sortable: true,
                render: (value) => new Date(value).toLocaleDateString(),
              },
              { key: 'customer', header: 'Customer', sortable: true },
              {
                key: 'amount',
                header: 'Amount',
                sortable: true,
                align: 'right' as const,
                render: (value) => `$${value.toLocaleString()}`,
              },
              {
                key: 'status',
                header: 'Status',
                render: (value) => (
                  <Badge
                    color={
                      value === 'completed'
                        ? 'green'
                        : value === 'pending'
                          ? 'yellow'
                          : value === 'failed'
                            ? 'red'
                            : 'gray'
                    }
                  >
                    {value}
                  </Badge>
                ),
              },
              { key: 'category', header: 'Category', sortable: true },
            ]}
            filterPresets={[
              { label: 'Completed', filter: (t) => t.status === 'completed' },
              { label: 'Pending', filter: (t) => t.status === 'pending' },
              { label: 'Failed', filter: (t) => t.status === 'failed' },
              { label: 'Large (>$50k)', filter: (t) => t.amount > 50000 },
            ]}
            searchable
            searchKeys={['id', 'customer', 'category']}
            exportable
            exportFilename="transactions-export"
            pageSize={15}
          />

          <Code block>
{`<DataTableWidget
  title="Transactions"
  data={transactions}
  columns={[
    { key: 'id', header: 'ID' },
    {
      key: 'amount',
      align: 'right',
      render: (value) => \`$\${value.toLocaleString()}\`
    }
  ]}
  filterPresets={[
    { label: 'Large', filter: (t) => t.amount > 50000 }
  ]}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Inventory Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Product Inventory Table</Title>
          <Text size="sm" c="dimmed">
            Inventory management table with stock status indicators and supplier filtering.
          </Text>

          <DataTableWidget
            title="Product Inventory"
            subtitle="100 products in stock"
            data={mockProducts}
            columns={[
              { key: 'sku', header: 'SKU', sortable: true, width: 120 },
              { key: 'name', header: 'Product Name', sortable: true },
              { key: 'category', header: 'Category', sortable: true },
              {
                key: 'stock',
                header: 'Stock',
                sortable: true,
                align: 'right' as const,
              },
              {
                key: 'status',
                header: 'Status',
                render: (value) => (
                  <Badge
                    color={
                      value === 'in-stock'
                        ? 'green'
                        : value === 'low-stock'
                          ? 'yellow'
                          : 'red'
                    }
                  >
                    {value.replace('-', ' ')}
                  </Badge>
                ),
              },
              {
                key: 'price',
                header: 'Price',
                sortable: true,
                align: 'right' as const,
                render: (value) => `$${value}`,
              },
              { key: 'supplier', header: 'Supplier', sortable: true },
            ]}
            filterPresets={[
              { label: 'In Stock', filter: (p) => p.status === 'in-stock' },
              { label: 'Low Stock', filter: (p) => p.status === 'low-stock' },
              { label: 'Out of Stock', filter: (p) => p.status === 'out-of-stock' },
              { label: 'Premium Parts Ltd', filter: (p) => p.supplier === 'Premium Parts Ltd' },
            ]}
            searchable
            searchKeys={['name', 'sku', 'category', 'supplier']}
            exportable
            exportFilename="inventory-export"
            pageSize={12}
          />

          <Code block>
{`<DataTableWidget
  title="Product Inventory"
  data={products}
  columns={[
    { key: 'sku', header: 'SKU' },
    { key: 'name', header: 'Product Name' },
    {
      key: 'status',
      render: (value) => <Badge color={statusColor}>{value}</Badge>
    }
  ]}
  filterPresets={[
    { label: 'Low Stock', filter: (p) => p.status === 'low-stock' }
  ]}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Task Management Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Task Management Table</Title>
          <Text size="sm" c="dimmed">
            Project task table with priority and status filtering.
          </Text>

          <DataTableWidget
            title="Project Tasks"
            subtitle="120 total tasks across all projects"
            data={mockTasks}
            columns={[
              { key: 'title', header: 'Task', sortable: true },
              { key: 'assignee', header: 'Assignee', sortable: true },
              {
                key: 'priority',
                header: 'Priority',
                sortable: true,
                render: (value) => (
                  <Badge
                    color={
                      value === 'critical'
                        ? 'red'
                        : value === 'high'
                          ? 'orange'
                          : value === 'medium'
                            ? 'yellow'
                            : 'gray'
                    }
                  >
                    {value}
                  </Badge>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                sortable: true,
                render: (value) => (
                  <Badge
                    variant="light"
                    color={
                      value === 'done'
                        ? 'green'
                        : value === 'review'
                          ? 'blue'
                          : value === 'in-progress'
                            ? 'yellow'
                            : 'gray'
                    }
                  >
                    {value.replace('-', ' ')}
                  </Badge>
                ),
              },
              { key: 'project', header: 'Project', sortable: true },
              {
                key: 'dueDate',
                header: 'Due Date',
                sortable: true,
                render: (value) => new Date(value).toLocaleDateString(),
              },
            ]}
            filterPresets={[
              { label: 'High Priority', filter: (t) => t.priority === 'high' || t.priority === 'critical' },
              { label: 'In Progress', filter: (t) => t.status === 'in-progress' },
              { label: 'Completed', filter: (t) => t.status === 'done' },
              { label: 'Website Redesign', filter: (t) => t.project === 'Website Redesign' },
            ]}
            searchable
            searchKeys={['title', 'assignee', 'project']}
            exportable
            exportFilename="tasks-export"
            pageSize={10}
          />

          <Code block>
{`<DataTableWidget
  title="Project Tasks"
  data={tasks}
  columns={[
    { key: 'title', header: 'Task' },
    {
      key: 'priority',
      render: (value) => <Badge color={priorityColor}>{value}</Badge>
    }
  ]}
  filterPresets={[
    {
      label: 'High Priority',
      filter: (t) => t.priority === 'high' || t.priority === 'critical'
    }
  ]}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                User Management
              </Text>
              <Text size="xs" c="dimmed">
                Manage users, roles, and permissions with search and filtering capabilities.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Transaction History
              </Text>
              <Text size="xs" c="dimmed">
                View and analyze financial transactions with export to CSV/Excel for reports.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Inventory Tracking
              </Text>
              <Text size="xs" c="dimmed">
                Monitor product stock levels and identify low inventory items quickly.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Project Management
              </Text>
              <Text size="xs" c="dimmed">
                Track tasks across projects with priority and status filtering.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Data Export
              </Text>
              <Text size="xs" c="dimmed">
                Export filtered data to CSV or Excel for further analysis or reporting.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Quick Filters
              </Text>
              <Text size="xs" c="dimmed">
                Create preset filters for common queries to speed up data exploration.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
